export interface MembershipSummary {
  /** Opaque handle for this specific membership row — pass this (never companyId) to switchCompany. */
  membershipId: string;
  companyId: string;
  companyName: string;
  roleId: string;
  roleName: string;
  /** EPN-36 — raw facts only, mirroring the backend `MembershipSummary` record exactly: whether this
   * membership's company was created through Partner Sign Up (EPN-35), and whether the company itself is
   * currently active. Neither is a policy decision by itself — see
   * features/partner-signin/partnerEligibility.ts for the one place that turns these into "can this
   * membership enter the Partner experience." The real backend always serializes both — optional here
   * only so the many pre-existing mock gateways/fixtures across this codebase that construct a
   * `MembershipSummary` literal don't all need updating for a fact they don't otherwise care about; treat
   * a missing value as `false`/`true` respectively (see `evaluatePartnerEligibility`), never read anywhere
   * outside Partner Sign In's own routing. */
  isPartnerChannel?: boolean;
  companyIsActive?: boolean;
}

export interface LoginResponse {
  userId: string;
  email: string;
  memberships: MembershipSummary[];
}

/** The enrolled MFA method names `/api/auth/login`, `/mfa/challenge`, and `/mfa/verify` all pass around
 * as plain strings (`MfaMethod.ToString()` on the backend) — "Email" or "Authenticator" today. Never
 * "Sms": see `MfaEndpoints`' own filtering comment on the backend for why that method is never offered
 * or accepted, defensively, even if an enrollment row for it somehow existed. */
export type MfaMethodName = string;

/** Body of `POST /api/auth/login`. `rememberMe` controls only the session cookies' own `Expires`
 * attribute (see `LoginRequest`'s doc comment on the backend) — it never changes server-side token
 * validity. */
export interface LoginRequestBody {
  email: string;
  password: string;
  rememberMe?: boolean;
}

/** One of the three possible outcomes of `POST /api/auth/login` — see `IAuthService.LoginOutcome`'s own
 * doc comment on the backend for the full discriminated-result reasoning. A plain 401 (unknown email,
 * wrong password, or inactive user) is deliberately NOT part of this union — `login()` still throws/
 * rejects an `ApiError` for that, unchanged, so a caller can't learn anything from the outcome shape
 * that a normal 401 wouldn't already hide. */
export type LoginOutcome =
  | ({ kind: 'success' } & LoginResponse)
  | { kind: 'mfaRequired'; challengeToken: string; availableMethods: MfaMethodName[] }
  | { kind: 'locked'; lockoutEndUtc: string };

/** Body of `POST /api/auth/mfa/verify`. No `rememberMe` field exists here by design — a completed MFA
 * login always issues a normal persistent session, the same as `rememberMe: true` would at plain
 * `/login` (see `MfaEndpoints`' own doc comment on the backend for this documented scope
 * simplification). `rememberDevice` is a wholly separate mechanism (the `epn_td` trusted-device cookie,
 * independent of session-cookie lifetime). */
export interface MfaVerifyRequestBody {
  challengeToken: string;
  method: MfaMethodName;
  code: string;
  rememberDevice: boolean;
}

/** EPN-99 follow-up — a company the caller still has an active membership in, but which is itself inactive. It is
 * never offered for selection and grants no access; only its name is returned so the person can be told their
 * business is inactive and to contact Escochecks to choose a plan. Optional here only so the many pre-existing mock
 * gateways/fixtures that build a `MeResponse` literal don't all need updating; treat a missing value as none. */
export interface InactiveCompanyNotice {
  companyName: string;
}

export interface MeResponse {
  userId: string;
  email: string;
  displayName: string | null;
  activeCompanyId: string | null;
  activeRoleId: string | null;
  memberships: MembershipSummary[];
  inactiveCompanies?: InactiveCompanyNotice[];
  /** EPN-240: permission codes of the ACTIVE membership only, for hiding or disabling actions the person cannot perform. DISPLAY ONLY: every server
   * route still enforces its own permission, so nothing may treat this list as an authorization decision. Absent (older server, or the mock identity)
   * means "unknown": show everything and let the server's 403 decide. */
  activePermissions?: string[];
  /** The ACTIVE company's own IANA time zone (for example `America/Los_Angeles`), or null when it has not chosen one yet (callers then use UTC and
   * say so). Optional for the same reason as `activePermissions`: older servers and many mock fixtures do not send it. It decides where a day or a
   * reporting month begins and ends; it never changes a stored amount or a check date. */
  activeCompanyTimeZoneId?: string | null;
  /** Present ONLY for a partner user working inside one client company. The page cannot read the httpOnly delegated token, so this is how it learns the client, the firm, the person's role, the scopes the
   * token holds, when it ends and whether the server still considers it valid. DISPLAY ONLY: every module authorizes every request. */
  partnerDelegation?: PartnerDelegationInfo | null;
}

export interface PartnerDelegationInfo {
  relationshipId: string;
  partnerOrganizationId: string;
  clientName: string;
  partnerFirmName: string;
  partnerRole: string;
  scopes: string[];
  expiresAtUtc: string;
  isStillValid: boolean;
}

export interface CompanyTimeZoneResponse {
  timeZoneId: string | null;
}

export interface CompanyResponse {
  id: string;
  name: string;
  ein: string | null;
  isActive: boolean;
  legacyBusinessId: number | null;
}

export interface MembershipResponse {
  id: string;
  userId: string;
  email: string;
  roleId: string;
  roleName: string;
  isActive: boolean;
}

export interface AuditEventRecord {
  id: string;
  occurredAtUtc: string;
  actorUserId: string | null;
  actorEmailSnapshot: string | null;
  actorType: string;
  companyId: string | null;
  eventType: string;
  targetType: string | null;
  targetId: string | null;
  metadataJson: string | null;
  correlationId: string;
}

export interface RoleResponse {
  id: string;
  name: string;
  isSystemRole: boolean;
}

export interface PermissionResponse {
  id: string;
  code: string;
  description: string;
}

export interface AuditEventPage {
  items: AuditEventRecord[];
  page: number;
  pageSize: number;
  totalCount: number;
}

/** EPN-49 — real MFA enrollment management. `method` is always `"Authenticator"` today; Email/SMS have no
 * real enrollment path (see docs/epn-49-owner-security-settings-gaps.md). Never carries a secret. */
export interface MfaEnrollmentResponse {
  method: string;
  isEnabled: boolean;
  enrolledAtUtc: string;
  disabledAtUtc: string | null;
}

/** The raw secret round-trips through the client between begin and confirm rather than being persisted
 * unconfirmed — see `ISecuritySettingsService`'s own doc comment on the backend for why. */
export interface BeginAuthenticatorEnrollmentResponse {
  secret: string;
  otpAuthUri: string;
}

export interface TrustedDeviceResponse {
  id: string;
  createdAtUtc: string;
  lastUsedAtUtc: string | null;
  createdByIp: string | null;
}

/** Thrown by a gateway method on a non-2xx response, carrying enough for the UI to react (e.g. a
 * 403 from switch-company means "no membership," not a generic failure).
 *
 * `operationId` is the backend's own `X-Operation-Id` response header (EPN-24A) exactly as received on the
 * response that was actually surfaced — never generated client-side; `null` when no response was received
 * or the header was absent/empty.
 *
 * `messageIsServerAuthored` is `false` only when `message` is a frontend-generated technical fallback
 * ("Request to /api/... failed with 400") because the response carried no usable problem-details `detail`;
 * such text must never be shown to a user. It defaults to `true` so a hand-constructed ApiError (mock
 * gateways, tests) keeps its authored message. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly operationId: string | null = null,
    public readonly messageIsServerAuthored: boolean = true,
    /** The stable machine-readable `code` an API sends in its problem body (for example
     * `SUBSCRIPTION_ACCESS_RESTRICTED` or `BILLING_UNAVAILABLE`), when it sent one. */
    public readonly code: string | null = null,
    /** The parsed problem body, for callers that need a field beyond `code`/`detail`. Never shown to users. */
    public readonly details: Record<string, unknown> | null = null,
  ) {
    super(message);
  }
}
